'use client';

import { useRef, useState, type FC } from 'react';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { CriteoProductCardItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/CriteoProductCardItemFragmentFragmentDoc';
import { ProductCardGridItem } from '@packages/modules/src/ProductCardV2/ProductCardV2.GridItem.client';

import { useOnFirstBecameVisible } from '../../hooks/useOnFirstBecameVisible';
import { CriteoBeaconImage } from '../CriteoBeaconImage/CriteoBeaconImage';
import type { CriteoProductCardContentProps } from './CriteoProductCard.Content';
import { CriteoProductCardContent } from './CriteoProductCard.Content';

/* GraphQL */ `
  fragment CriteoProductCardItemFragment on SponsoredStylePreviewItem {
    ...CriteoProductCardContentFragment
    styleId
    tracking {
      onLoad
      onView
    }
  }
`;

export type CriteoProductCardProps = Omit<
  CriteoProductCardContentProps,
  'maskedItem' | 'hasReceivedFocus'
> & {
  maskedItem: FragmentType<typeof CriteoProductCardItemFragmentFragmentDoc>;
  onFirstBecameVisible?: (parameters: { pageNumber: number; indexInPage?: number }) => void;
};

/**
 * ProductCard component used to render sponsored products
 * */
export const CriteoProductCard: FC<CriteoProductCardProps> = ({
  onFirstBecameVisible,
  maskedItem,
  ...contentProps
}) => {
  const { pageNumber } = contentProps;

  const [hasReceivedFocus, setHasReceivedFocus] = useState(false);
  const item = unmask<typeof CriteoProductCardItemFragmentFragmentDoc>(maskedItem);

  const ref = useRef<HTMLLIElement>(null);
  useOnFirstBecameVisible(ref, () => {
    onFirstBecameVisible?.({ pageNumber });
  });

  return (
    <ProductCardGridItem
      className="group pb-2"
      ref={ref}
      id={item.styleId}
      onFocus={() => {
        setHasReceivedFocus(true);
      }}
    >
      <CriteoProductCardContent
        hasReceivedFocus={hasReceivedFocus}
        maskedItem={item}
        {...contentProps}
      />

      {item.tracking.onLoad && <CriteoBeaconImage src={item.tracking.onLoad} name="onLoad" />}
      {item.tracking.onView && <CriteoBeaconImage src={item.tracking.onView} name="onView" />}
    </ProductCardGridItem>
  );
};
